import type { ChatAttachment } from '../domain/assistant';

const MAX_EDGE = 1800;
const MAX_PDF_BYTES = 8 * 1024 * 1024;

function readAsDataUrl(file: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result));
    r.onerror = () => reject(new Error('Could not read the file.'));
    r.readAsDataURL(file);
  });
}

/**
 * Prepare a photo or PDF for Elio. Photos are downscaled and re-encoded as JPEG in the browser,
 * which also strips location and camera metadata before anything leaves the device.
 */
export async function toAttachment(file: File): Promise<ChatAttachment> {
  const name = file.name.slice(0, 120) || 'attachment';
  if (file.type === 'application/pdf' || /\.pdf$/i.test(file.name)) {
    if (file.size > MAX_PDF_BYTES) throw new Error('That PDF is larger than 8 MB. Send only the pages you need.');
    const dataUrl = await readAsDataUrl(file);
    return { name, kind: 'pdf', dataUrl: dataUrl.replace(/^data:[^;]*;/, 'data:application/pdf;') };
  }
  if (!file.type.startsWith('image/')) throw new Error('Elio can read photos, screenshots and PDFs.');
  const bitmap = await createImageBitmap(file).catch(() => null);
  if (!bitmap) throw new Error('This image format could not be opened. Try a JPEG or PNG screenshot.');
  const scale = Math.min(1, MAX_EDGE / Math.max(bitmap.width, bitmap.height));
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(bitmap.width * scale);
  canvas.height = Math.round(bitmap.height * scale);
  canvas.getContext('2d')!.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  bitmap.close?.();
  const dataUrl = canvas.toDataURL('image/jpeg', 0.85);
  return { name: name.replace(/\.(heic|heif|png|webp|gif)$/i, '.jpg'), kind: 'image', dataUrl };
}
